import {AbstractControl} from '@angular/forms';
import {TextAutocompleteConfig, TextAutocompleteItem, TextAutocompleteGroup} from './text-autocomplete.types';
import {ChangeDetectorRef} from '@angular/core';
import {TextAutocompletePanelComponent} from './text-autocomplete-panel/text-autocomplete-panel.component';
import {TranslateService} from '@ngx-translate/core';
import { getTextAutocompletePanelPosition } from './text-autocomplete-position.util';

export interface InternalTextAutocompleteItem extends TextAutocompleteItem {
    groupLabel?: string;
    groupIndex?: number;
    groupDisabled?: boolean;
    globalIndex?: number;
}

export interface InternalTextAutocompleteGroup {
    label?: string;
    disabled?: boolean;
    items: InternalTextAutocompleteItem[];
}

/**
 * Reusable controller for trigger-based autocomplete in text and textarea fields.
 * Encapsulates filtering, keyboard navigation, and insertion logic.
 */
export class TextInputAutocompleteController {
    readonly filteredAutocompleteItems: InternalTextAutocompleteItem[] = [];
    readonly filteredAutocompleteGroups: InternalTextAutocompleteGroup[] = [];
    autocompleteSearchText = '';
    focusedAutocompleteIndex = -1;

    private autocompleteItems: InternalTextAutocompleteItem[] = [];
    private triggerStart = '{{';
    private triggerEnd = '}}';
    private activeTriggerStartIndex: number | null = null;
    private currentCaretIndex: number | null = null;
    private suppressNextPanelClosed = false;
    private config?: TextAutocompleteConfig;

    private resizeObserver?: ResizeObserver;
    private observedResizeElements: HTMLElement[] = [];
    private observedResizeSizes = new Map<HTMLElement, {width: number; height: number}>();
    private pendingResizeRaf: number | null = null;

    constructor(
        private readonly control: AbstractControl,
        private readonly getInputElement: () => HTMLInputElement | HTMLTextAreaElement | null,
        private readonly getPanel: () => TextAutocompletePanelComponent | null,
        private readonly containerSelector: string,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly translateService: TranslateService
    ) {}

    setup(config: TextAutocompleteConfig | undefined): void {
        this.config = config;
        const hasFlatItems = !!config?.items && config.items.length > 0;
        const hasGroups = !!config?.groupedItems && config.groupedItems.length > 0;

        if (!config || (!hasFlatItems && !hasGroups)) {
            this.autocompleteItems = [];
            this.clearFiltered();
            this.triggerStart = '{{';
            this.triggerEnd = '}}';
            return;
        }

        const items: InternalTextAutocompleteItem[] = [];

        if (hasFlatItems) {
            config.items!.forEach((item) => items.push({...item}));
        }

        if (hasGroups) {
            config.groupedItems!.forEach((group: TextAutocompleteGroup, groupIndex: number) => {
                if (!group?.items?.length) return;
                group.items.forEach((item) => {
                    items.push({
                        ...item,
                        groupLabel: group.label,
                        groupIndex,
                        groupDisabled: !!group.disabled
                    });
                });
            });
        }

        this.autocompleteItems = items;
        this.triggerStart = config.triggerStart ?? '{{';
        this.triggerEnd = config.triggerEnd ?? '}}';
        this.filteredAutocompleteItems.length = 0;
        this.filteredAutocompleteItems.push(...this.autocompleteItems);
        this.rebuildFilteredGroups();
        this.changeDetectorRef.markForCheck();
    }

    onInput(value: string, caretIndex: number): void {
        const config = this.config;
        if (!config) return;
        const triggerStart = this.triggerStart;
        const triggerEnd = this.triggerEnd;
        const previousValue = this.control.value ?? '';
        let newValue = value;
        let newCaretIndex = caretIndex;

        if (triggerStart && triggerEnd && caretIndex >= triggerStart.length) {
            const justTyped = value.substring(caretIndex - triggerStart.length, caretIndex);
            // If user just added the triggerStart sequence, try to insert the triggerEnd automatically.
            if (justTyped === triggerStart && value.length > previousValue.length) {
                
                // Check if there is a triggerEnd already in the same line
                const {lineStart, lineEnd} = this.getLineRangeAt(value, caretIndex);
                const nextStartIndex = this.getIndexOfWithinLine(value, triggerStart, caretIndex, lineStart, lineEnd);
                const searchLimit = nextStartIndex === -1 ? lineEnd : nextStartIndex;
                const endIndex = this.getIndexOfWithinLine(value, triggerEnd, caretIndex, lineStart, lineEnd);
                const hasEndBeforeNextStart = endIndex !== -1 && endIndex < searchLimit;

                if (!hasEndBeforeNextStart) {

                    newValue = value.slice(0, caretIndex) + triggerEnd + value.slice(caretIndex);
                    newCaretIndex = caretIndex; // keep caret between start and end

                    this.control.setValue(newValue);

                    const inputEl = this.getInputElement();
                    if (inputEl) {
                        setTimeout(() => {
                            inputEl.focus();
                            inputEl.setSelectionRange(newCaretIndex, newCaretIndex);
                        });
                    }
                }
            }
        }
        this.updateForPosition(newValue, newCaretIndex);
    }

    onFieldClick(value: string, caretIndex: number): void {
        const config = this.config;
        if (!config) return;

        this.updateForPosition(value, caretIndex);
    }

    onKeydown(event: KeyboardEvent): void {
        const config = this.config;
        if (!config || this.filteredAutocompleteItems.length === 0) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            event.stopPropagation();
            this.focusIndex(this.getNextFocusableIndex(1));
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            event.stopPropagation();
            this.focusIndex(this.getNextFocusableIndex(-1));
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            const item = this.filteredAutocompleteItems[this.focusedAutocompleteIndex];
            if (item != null && !item.disabled && !item.groupDisabled) {
                this.onOptionSelected(item);
            }
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.closePanel();
        }
    }

    onOptionSelected(item: TextAutocompleteItem): void {
        const internal = item as InternalTextAutocompleteItem;
        if (!item || this.activeTriggerStartIndex == null || internal.disabled || internal.groupDisabled) {
            this.closePanel();
            return;
        }

        const controlValue = this.control.value ?? '';
        const caretIndex = this.currentCaretIndex ?? controlValue.length;
        const triggerStart = this.triggerStart;
        const triggerEnd = this.triggerEnd;
        const startInnerIndex = this.activeTriggerStartIndex + triggerStart.length;
        const {lineStart, lineEnd} = this.getLineRangeAt(controlValue, this.activeTriggerStartIndex);
        const endIndex = this.getIndexOfWithinLine(controlValue, triggerEnd, startInnerIndex, lineStart, lineEnd);

        const beforeTrigger = controlValue.substring(0, startInnerIndex);
        let valueToInsert = item.value;

        // Determine where to stop replacing:
        // - If we still have a closing trigger and the caret is inside that token,
        //   replace only the inner content up to the closing trigger.
        // - If the closing trigger was deleted (or caret is outside), replace only
        //   up to the current caret position, leaving everything after the caret.
        let replaceEnd: number;
        if (endIndex !== -1 && caretIndex <= endIndex) {
            replaceEnd = endIndex;
        } else {
            replaceEnd = caretIndex;
            valueToInsert += triggerEnd;
        }

        const afterPart = controlValue.substring(replaceEnd);
        const newValue = `${beforeTrigger}${valueToInsert}${afterPart}`;

        this.control.setValue(newValue, {emitEvent: false});
        this.control.markAsDirty();
        this.control.updateValueAndValidity();
        this.closePanel();

        const inputEl = this.getInputElement();
        if (inputEl) {
            const cursorPosition = beforeTrigger.length + valueToInsert.length;
            setTimeout(() => {
                inputEl.focus();
                inputEl.setSelectionRange(cursorPosition, cursorPosition);
            });
        }
        this.changeDetectorRef.markForCheck();
    }

    onPanelOpened(): void {
        const config = this.config;
        if (!config || this.activeTriggerStartIndex == null) {
            this.closePanel();
            return;
        }
        if (config.autoActiveFirstOption) {
            this.focusIndex(this.getFirstFocusableIndex());
        }
        this.changeDetectorRef.markForCheck();
    }

    setFocusedIndex(index: number): void {
        this.focusedAutocompleteIndex = index;
        this.changeDetectorRef.markForCheck();
    }

    onPanelClosed(): void {
        if (this.suppressNextPanelClosed) {
            this.suppressNextPanelClosed = false;
            this.changeDetectorRef.markForCheck();
            return;
        }
        this.disconnectResizeObserver();
        this.clearFiltered();
        this.activeTriggerStartIndex = null;
        this.currentCaretIndex = null;
        this.focusedAutocompleteIndex = -1;
        this.changeDetectorRef.markForCheck();
    }

    closePanel(): void {
        this.onPanelClosed();
        const panel = this.getPanel();
        if (panel?.isOpen) {
            panel.close();
        }
    }

    destroy(): void {
        // Component using this controller is being destroyed.
        // Ensure any observers/raf callbacks are cleaned up even if panel never closed.
        this.disconnectResizeObserver();
        this.activeTriggerStartIndex = null;
        this.currentCaretIndex = null;
        this.suppressNextPanelClosed = false;
    }

    private clearFiltered(): void {
        this.filteredAutocompleteItems.length = 0;
        this.filteredAutocompleteGroups.length = 0;
        this.autocompleteSearchText = '';
    }

    private updateForPosition(value: string, caretIndex: number): void {
        const triggerStart = this.triggerStart;
        const triggerEnd = this.triggerEnd;
        const {lineStart, lineEnd} = this.getLineRangeAt(value, caretIndex);
        const lastTriggerIndex = this.getLastIndexOfWithinLine(
            value,
            triggerStart,
            caretIndex,
            lineStart,
            lineEnd
        );

        if (lastTriggerIndex === -1) {
            this.closePanel();
            return;
        }

        const startInnerIndex = lastTriggerIndex + triggerStart.length;
        const endIndex = this.getIndexOfWithinLine(value, triggerEnd, startInnerIndex, lineStart, lineEnd);

        if (endIndex !== -1 && (caretIndex < startInnerIndex || caretIndex > endIndex)) {
            this.closePanel();
            return;
        }

        // Filter using the full inner token when closing trigger exists (caret is inside that span).
        // Without a closing trigger yet, only the text from the opening trigger to the caret counts.
        let searchText: string;
        if (endIndex !== -1) {
            searchText = value.substring(startInnerIndex, endIndex);
        } else {
            searchText = value.substring(startInnerIndex, caretIndex);
            if (searchText.indexOf(triggerEnd) !== -1) {
                this.closePanel();
                return;
            }
        }

        this.activeTriggerStartIndex = lastTriggerIndex;
        this.currentCaretIndex = caretIndex;
        const trimmedSearchText = searchText.trim();
        this.autocompleteSearchText = trimmedSearchText;
        const position = getTextAutocompletePanelPosition(this.getInputElement(), caretIndex, this.containerSelector);
        if (position) this.getPanel()?.setPosition(position);
        this.ensureResizeObserverWatchingActiveInput();

        const loweredSearch = trimmedSearchText.toLowerCase();
        this.filteredAutocompleteItems.length = 0;
        this.filteredAutocompleteItems.push(
            ...this.autocompleteItems.filter((item) => {
                const descriptionPart = (item.description || '').toLowerCase();
                const translatedDescriptionPart = this.translateService.instant(item.description).toLowerCase();
                const valuePart = (item.value || '').toLowerCase();
                const combined = `${descriptionPart !== translatedDescriptionPart ? translatedDescriptionPart : descriptionPart} ${valuePart}`.trim();
                return !loweredSearch || combined.indexOf(loweredSearch) !== -1;
            })
        );

        if (this.filteredAutocompleteItems.length === 0) {
            this.closePanel();
            return;
        }

        this.rebuildFilteredGroups();
        const panel = this.getPanel();
        if (panel && !panel.isOpen) {
            panel.open();
        }
        const config = this.config;
        if (config?.autoActiveFirstOption) {
            this.focusIndex(this.getFirstFocusableIndex());
        }
        this.changeDetectorRef.markForCheck();
    }

    private ensureResizeObserverWatchingActiveInput(): void {
        if (typeof window === 'undefined') return;
        if (typeof (window as any).ResizeObserver === 'undefined') return;

        const inputEl = this.getInputElement();
        if (!inputEl) return;

        const wrapper = inputEl.closest('.mat-form-field-wrapper') as HTMLElement | null;
        const nextObserved = [inputEl as unknown as HTMLElement, wrapper].filter(Boolean) as HTMLElement[];

        const sameElements =
            nextObserved.length === this.observedResizeElements.length &&
            nextObserved.every((el, idx) => el === this.observedResizeElements[idx]);
        if (sameElements) return;

        if (!this.resizeObserver) {
            this.resizeObserver = new ResizeObserver((entries) => {
                // Only reposition while the panel is logically active.
                if (this.activeTriggerStartIndex == null || this.currentCaretIndex == null) return;

                let sizeChanged = false;
                for (const entry of entries) {
                    const el = entry.target as HTMLElement;
                    const rect = el.getBoundingClientRect();
                    const next = {width: rect.width, height: rect.height};
                    const prev = this.observedResizeSizes.get(el);
                    if (
                        prev == null ||
                        Math.abs(prev.width - next.width) > 0.5 ||
                        Math.abs(prev.height - next.height) > 0.5
                    ) {
                        this.observedResizeSizes.set(el, next);
                        sizeChanged = true;
                    }
                }

                if (!sizeChanged) return;
                if (this.pendingResizeRaf != null) return;

                this.pendingResizeRaf = window.requestAnimationFrame(() => {
                    this.pendingResizeRaf = null;
                    const currentValue = this.control.value ?? '';
                    const caret = this.currentCaretIndex ?? currentValue.length;
                    this.updateForPosition(currentValue, caret);
                });
            });
        }

        // Detach previous elements
        this.observedResizeElements.forEach((el) => {
            try {
                this.resizeObserver!.unobserve(el);
            } catch {
                // ignore
            }
        });
        this.observedResizeElements = nextObserved;
        this.observedResizeSizes.clear();

        // Attach new elements
        this.observedResizeElements.forEach((el) => {
            const rect = el.getBoundingClientRect();
            this.observedResizeSizes.set(el, {width: rect.width, height: rect.height});
            this.resizeObserver!.observe(el);
        });
    }

    private disconnectResizeObserver(): void {
        if (typeof window !== 'undefined' && this.pendingResizeRaf != null) {
            window.cancelAnimationFrame(this.pendingResizeRaf);
        }
        this.pendingResizeRaf = null;

        if (this.resizeObserver) {
            this.observedResizeElements.forEach((el) => {
                try {
                    this.resizeObserver!.unobserve(el);
                } catch {
                    // ignore
                }
            });
        }
        this.observedResizeElements = [];
        this.observedResizeSizes.clear();
    }

    private focusIndex(index: number): void {
        const count = this.filteredAutocompleteItems.length;
        if (count === 0) return;
        this.focusedAutocompleteIndex = Math.max(0, Math.min(index, count - 1));
        const panel = this.getPanel();
        panel?.scrollItemIntoView(this.focusedAutocompleteIndex);
        this.changeDetectorRef.markForCheck();
    }

    private getFirstFocusableIndex(): number {
        const list = this.filteredAutocompleteItems;
        for (let i = 0; i < list.length; i++) {
            if (!list[i].disabled && !list[i].groupDisabled) return i;
        }
        return 0;
    }

    private getNextFocusableIndex(direction: 1 | -1): number {
        const list = this.filteredAutocompleteItems;
        if (!list.length) return 0;
        const start = this.focusedAutocompleteIndex >= 0 ? this.focusedAutocompleteIndex : (direction === 1 ? -1 : list.length);
        let i = start + direction;
        while (i >= 0 && i < list.length) {
            if (!list[i].disabled && !list[i].groupDisabled) return i;
            i += direction;
        }
        return Math.max(0, Math.min(start, list.length - 1));
    }

    private rebuildFilteredGroups(): void {
        this.filteredAutocompleteItems.forEach((item, idx) => {
            item.globalIndex = idx;
        });

        const groupsMap = new Map<string, InternalTextAutocompleteGroup>();
        const ungroupedKey = '__ungrouped__';

        this.filteredAutocompleteItems.forEach((item) => {
            const hasGroup = typeof item.groupIndex === 'number';
            const key = hasGroup ? String(item.groupIndex) : ungroupedKey;
            if (!groupsMap.has(key)) {
                groupsMap.set(key, {
                    label: hasGroup ? item.groupLabel : undefined,
                    disabled: hasGroup ? !!item.groupDisabled : false,
                    items: []
                });
            }
            groupsMap.get(key)!.items.push(item);
        });

        this.filteredAutocompleteGroups.length = 0;
        if (groupsMap.has(ungroupedKey)) {
            this.filteredAutocompleteGroups.push(groupsMap.get(ungroupedKey)!);
        }
        Array.from(groupsMap.keys())
            .filter((k) => k !== ungroupedKey)
            .sort((a, b) => Number(a) - Number(b))
            .forEach((key) => this.filteredAutocompleteGroups.push(groupsMap.get(key)!));
    }

    private getLastIndexOfWithinLine(
        value: string,
        search: string,
        beforeIndex: number,
        lineStart: number,
        lineEnd: number
    ): number {
        if (!search) return -1;
        
        const limit = Math.min(beforeIndex, lineEnd);
        let last = -1;
        let pos = lineStart;
        while (pos < limit) {
            const idx = value.indexOf(search, pos);
            if (idx === -1 || idx >= limit) break;
            last = idx;
            pos = idx + search.length;
        }
        return last;
    }

    private getIndexOfWithinLine(
        value: string,
        search: string,
        fromIndex: number,
        lineStart: number,
        lineEnd: number
    ): number {
        if (!search) return -1;
        
        const idx = value.indexOf(search, Math.max(fromIndex, lineStart));
        if (idx === -1 || idx >= lineEnd) return -1;
        
        return idx;
    }

    private getLineRangeAt(value: string, index: number) {
        const safeIndex = Math.max(0, Math.min(index, value.length));
        let lineStart = safeIndex;
        while (lineStart > 0 && !this.isLineBreakChar(value.charAt(lineStart - 1))) {
            lineStart--;
        }
        let lineEnd = safeIndex;
        while (lineEnd < value.length && !this.isLineBreakChar(value.charAt(lineEnd))) {
            lineEnd++;
        }
        return {lineStart, lineEnd};
    }
    
    private isLineBreakChar(ch: string): boolean {
        return ch === '\n' || ch === '\r';
    }
}
